import { useEffect, useMemo, useState } from "react";
import { getWorkflowNodes } from "../services/workflowGeneration";
import { normalizeWorkflowNodes } from "../utils/workflowNodes";
import DynamicNode from "../nodes/DynamicNode";
import { Node } from "../nodes/Node";

/**
 * A React Flow `nodeTypes` map that renders every node with the server-driven DynamicNode,
 * bound to its definition, instead of the app's prebuilt components. `defaultNode` still
 * catches any type the server hasn't described.
 *
 * @param {boolean} enabled When false, returns just the default fallback so callers can switch
 *   between the prebuilt and dynamic renderers without an extra fetch.
 * @returns {{[type: string]: React.ComponentType}}
 */
export const useDynamicNodeTypes = (enabled = false) => {
  const [definitions, setDefinitions] = useState([]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    getWorkflowNodes()
      .then((result) => {
        if (cancelled || !result.success) return;
        setDefinitions(normalizeWorkflowNodes(result.payload));
      })
      .catch(() => {
        // Non-fatal: the default node still renders unknown types.
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return useMemo(() => {
    const map = { defaultNode: Node };

    definitions.forEach((definition) => {
      if (!definition.type) return;
      map[definition.type] = (props) => <DynamicNode {...props} definition={definition} />;
    });

    return map;
  }, [definitions]);
};

export default useDynamicNodeTypes;
